import React, { useCallback, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { fetchCompanyDetails } from "../../../../api/action/company.jsx";
import { getAllProductCategories, getAllProducts } from "../../../../api/action/products.jsx";
import ProductsList from "./ProductTiles.jsx";
import FilterPanel from "./FilterPanel.jsx";
import { academyLogger } from "utils/logger";

const Products = () => {
  const dispatch = useDispatch();
  const fullUrl = window.location.href;
  const location = useLocation();
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState([0, 0]);
  const [maxPrice, setMaxPrice] = useState(0);
  const [sortingOrder, setSortingOrder] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});

  const domain = fullUrl.split("/").slice(0, 3).join("/");
  const { name } = useParams();
  const companyPrimaryId = useSelector((state) => state.companyReducer.company?.primaryId);
  const [filteredCategoryProducts, setFilteredCategoryProducts] = useState([]);
  const searchQuery = new URLSearchParams(location.search).get("search")?.trim()?.toLowerCase() || "";

  const fetchProducts = useCallback(async (company, manualCategory = null) => {
    try {
      let products;

      // Fetch all products
      products = await getAllProducts(company)(dispatch);
      products = Array.isArray(products) ? products : [];

      // Filter products based on the determined category
      const categoryToUse = manualCategory || name;
      if (categoryToUse && categoryToUse !== "All") {
        products = products.filter(
          (product) => product.category?.name === categoryToUse
        );
      }

      // Log and store filtered products
      setFilteredCategoryProducts(products); // Store filtered products

      // Extract and format optionalAttributes (without filtering duplicates)
      const optionalAttributes = [];
      products.forEach((product) => {
        product.optionalAttributes?.forEach((attr) => {
          optionalAttributes.push({
            name: attr.name,
            values: attr.values.map((v) => v.value),
          });
        });
      });

      setCategoryProducts(products);
      setIsLoading(false);

      const res = await dispatch(getAllProductCategories(company));
      setCategories([{ code: "All", name: "All" }, ...(Array.isArray(res) ? res : [])]);

      const productPrices = products
        .map((product) => parseFloat(product.price?.priceAmt))
        .filter((price) => Number.isFinite(price));

      if (productPrices.length > 0) {
        const highestPrice = Math.max(...productPrices);
        const roundedMaxPrice = Math.ceil(highestPrice);
        setMaxPrice(roundedMaxPrice);
        setSelectedPriceRange([0, roundedMaxPrice]);
      } else {
        setMaxPrice(0);
        setSelectedPriceRange([0, 0]);
      }
    } catch (error) {
      academyLogger.error("Error fetching products:", error);
      setIsLoading(false);
    }
  }, [dispatch, name]);

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedPriceRange([0, maxPrice]);
    setSortingOrder(null);
    setSelectedAttributes({});
  };

  const handleCategoryChange = (filterVal) => {
    setSelectedCategories((prev) =>
      prev.includes(filterVal.code)
        ? prev.filter((code) => code !== filterVal.code)
        : [...prev, filterVal.code]
    );
  };

  const toggleSortingOrder = (order) => {
    setSortingOrder(order);
  };

  const handleAttributeChange = (attributeName, attributeValue) => {
    setSelectedAttributes((prev) => {
      const updatedAttributes = { ...prev };
      if (updatedAttributes[attributeName]) {
        if (updatedAttributes[attributeName].includes(attributeValue)) {
          updatedAttributes[attributeName] = updatedAttributes[attributeName].filter((val) => val !== attributeValue);
        } else {
          updatedAttributes[attributeName].push(attributeValue);
        }
      } else {
        updatedAttributes[attributeName] = [attributeValue];
      }
      return updatedAttributes;
    });
  };

  const filteredCateProducts = categoryProducts.filter((product) => {
    const priceAmt = parseFloat(product.price?.priceAmt);
    const categoryCode = product.category ? product.category.code : "";
    const searchableText = [
      product.name,
      product.description,
      product.category?.name,
      product.category?.code,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const attributeMatch = Object.keys(selectedAttributes).every((attrName) => {
      const selectedValues = selectedAttributes[attrName];
      const productValues = product.optionalAttributes?.find(
        (attr) => attr.name?.toLowerCase() === attrName.toLowerCase()
      )?.values?.map((v) => (typeof v === "string" ? v : v?.value)?.toLowerCase()).filter(Boolean) || [];

      return selectedValues.some((val) => productValues.includes(val.toLowerCase()));
    });

    return (
      (selectedCategories.length === 0 ||
        selectedCategories.includes(categoryCode) ||
        selectedCategories.includes("All")) &&
      (!searchQuery || searchableText.includes(searchQuery)) &&
      Number.isFinite(priceAmt) &&
      priceAmt >= selectedPriceRange[0] &&
      priceAmt <= selectedPriceRange[1] &&
      attributeMatch
    );
  });

  useLayoutEffect(() => {
    if (!companyPrimaryId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchCompanyDetails(domain)
      .then(() => {
        fetchProducts(companyPrimaryId, name);
      })
      .catch((error) => {
        academyLogger.error("Error fetching company details:", error);
        setIsLoading(false);
      });
  }, [companyPrimaryId, domain, fetchProducts, name]);

  return (
    <div className="flex flex-col items-center p-4 mt-16">
      {isLoading ? (
        <div className="flex items-center justify-center min-h-screen">
          {/* <Loader /> */}
        </div>
      ) : (
        <>
          <div className="flex w-full">
            <div className="filter-panel-container w-1/4 bg-white shadow-lg">
              <FilterPanel
                categories={categories}
                selectedCategories={selectedCategories}
                handleCategoryChange={handleCategoryChange}
                sortingOrder={sortingOrder}
                toggleSortingOrder={toggleSortingOrder}
                handleClearFilters={handleClearFilters}
                selectedPriceRange={selectedPriceRange}
                setSelectedPriceRange={setSelectedPriceRange}
                maxPrice={maxPrice}
                // optionalAttributes={optionalAttributes}
                selectedAttributes={selectedAttributes}
                handleAttributeChange={handleAttributeChange}
                filteredProducts={filteredCategoryProducts}
              />
            </div>

            <div className="flex flex-col w-full overflow-y-auto">
              <ProductsList
                products={filteredCateProducts}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Products;
